import { Box, Container, Group, Stack, Text, Title, Button, SimpleGrid, Anchor } from "@mantine/core";
import { Link } from "react-router-dom";
import { artist } from "@/content/artist";
import { releases, getSpotlight, isReleased, isComingSoon } from "@/content/releases";
import { liveArchive } from "@/content/live";
import { pressMentions } from "@/content/press";
import { SpotlightCard } from "@/components/release/SpotlightCard";
import { ReleaseCard } from "@/components/release/ReleaseCard";
import { LiveTile } from "@/components/live/LiveTile";
import { PageContainer } from "@/components/layout/PageContainer";
import { HOME_HERO_HEIGHT } from "@/lib/heroLayout";

export default function Home() {
  const spotlight = getSpotlight();
  const liveTeaser = liveArchive.filter((l) => l.format === "vr").slice(0, 3);
  const teaserReleases = releases.filter(isReleased).slice(0, 6);
  // Coming-soon teaser excludes the spotlight (which has its own big card)
  // so SWEET OUTSIDE doesn't appear twice.
  const comingSoonReleases = releases.filter((r) => isComingSoon(r) && !r.spotlight);

  return (
    <Box style={{ overflowX: "hidden" }}>
      {/* Full-bleed hero. The looping video sits behind this section in the
          persistent layer (AppShell → PersistentHeroBackdrop); this box just
          reserves the band height and centers the title over it. */}
      <Box
        component="section"
        className="hero-bleed"
        style={{
          position: "relative",
          minHeight: HOME_HERO_HEIGHT,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          overflow: "hidden",
        }}
      >
        <Container
          size="xl"
          px={{ base: "md", md: "xl" }}
          py="xl"
          style={{ position: "relative", zIndex: 1, width: "100%" }}
        >
          <Stack gap="lg" align="center" ta="center">
            <Title
              order={1}
              fz="clamp(2.75rem, 12vw, 9rem)"
              fw={700}
              className="iridescent-text"
              style={{
                whiteSpace: "nowrap",
                letterSpacing: "0.04em",
                lineHeight: 1,
                margin: 0,
              }}
            >
              7 MINUTES DEAD
            </Title>
            <Text size="lg" c="var(--color-starlight)" maw={640} ta="center">
              {artist.shortBio}
            </Text>
            <Group mt="md" justify="center">
              <Button component={Link} to="/releases" size="md" color="indigo">
                Listen
              </Button>
              <Button component={Link} to="/live" size="md" color="cyan">
                Watch
              </Button>
              <Button component={Link} to="/access" size="md" variant="outline" color="gray">
                Label / press access
              </Button>
            </Group>
          </Stack>
        </Container>
      </Box>

      <PageContainer>
        {/* Spotlight → About → Catalog → Coming soon → Live → Press → Booking. */}
        <Stack gap={64}>
          {spotlight && (
            <Box>
              <Text size="xs" tt="uppercase" c="dimmed" mb="sm">
                Now spotlighting
              </Text>
              <SpotlightCard release={spotlight} />
            </Box>
          )}

          <Box>
            <Title order={2} size="h3" mb="sm">
              about
            </Title>
            <Text size="md" c="var(--color-foam)" maw={760}>
              {artist.aboutTeaser ?? artist.mediumBio}
            </Text>
            <Anchor component={Link} to="/about" mt="xs" inline>
              Read more →
            </Anchor>
          </Box>

          <Box>
            <Group justify="space-between" mb="sm">
              <Title order={2} size="h3">
                live
              </Title>
              <Anchor component={Link} to="/live">
                Full archive →
              </Anchor>
            </Group>
            {liveTeaser.length > 0 ? (
              <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="md">
                {liveTeaser.map((s) => (
                  <LiveTile key={s.slug} show={s} />
                ))}
              </SimpleGrid>
            ) : (
              <Box
                p="xl"
                style={{
                  background: "var(--color-deep)",
                  border: "1px dashed var(--color-ocean)",
                  borderRadius: 12,
                }}
              >
                <Text c="dimmed" ta="center">
                  VR DJ-set clips coming soon.
                </Text>
              </Box>
            )}
          </Box>

          {teaserReleases.length > 0 && (
            <Box>
              <Group justify="space-between" mb="sm">
                <Title order={2} size="h3">
                  catalog
                </Title>
                <Anchor component={Link} to="/releases">
                  Full discography →
                </Anchor>
              </Group>
              <SimpleGrid cols={{ base: 2, sm: 3, md: 4, lg: 6 }} spacing="md">
                {teaserReleases.map((r) => (
                  <ReleaseCard key={r.slug} release={r} />
                ))}
              </SimpleGrid>
            </Box>
          )}

          {comingSoonReleases.length > 0 && (
            <Box>
              <Group justify="space-between" mb="sm">
                <Title order={2} size="h3">
                  coming soon
                </Title>
                <Anchor component={Link} to="/releases">
                  All upcoming →
                </Anchor>
              </Group>
              <SimpleGrid cols={{ base: 2, sm: 3, md: 4, lg: 6 }} spacing="md">
                {comingSoonReleases.map((r) => (
                  <ReleaseCard key={r.slug} release={r} />
                ))}
              </SimpleGrid>
            </Box>
          )}

          {pressMentions.length > 0 && (
            <Box>
              <Title order={2} size="h3" mb="sm">
                Press
              </Title>
              <SimpleGrid cols={{ base: 1, md: 3 }} spacing="md">
                {pressMentions.slice(0, 3).map((m) => (
                  <Anchor key={m.url} href={m.url} target="_blank" rel="noopener noreferrer">
                    <Box
                      p="md"
                      style={{
                        background: "var(--color-deep)",
                        border: "1px solid var(--color-ocean)",
                        borderRadius: 8,
                      }}
                    >
                      <Text size="xs" c="dimmed" tt="uppercase">
                        {m.outlet}
                      </Text>
                      <Text size="sm" lineClamp={2}>
                        {m.title}
                      </Text>
                    </Box>
                  </Anchor>
                ))}
              </SimpleGrid>
            </Box>
          )}

          <Box
            p="xl"
            style={{ background: "var(--color-deep)", borderRadius: 12, textAlign: "center" }}
          >
            <Text fw={600} mb="xs">
              booking · press · label inquiries
            </Text>
            <Button component={Link} to="/contact" variant="light" color="indigo">
              Get in touch
            </Button>
          </Box>
        </Stack>
      </PageContainer>
    </Box>
  );
}
